import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageSquare, Send, Eye, Edit, FileText } from 'lucide-react';
import { CollabProposal, User, Message, ProposalStatus } from '../types';
import { createDiffHtml } from '../lib/diff';
import { cn } from '@/lib/utils';

interface CollabNegotiationModalProps {
  isOpen: boolean;
  viewMode: ViewMode;
  proposal: CollabProposal | null;
  draftProposal: CollabProposal | null;
  previousProposal: CollabProposal | null; 
  currentUser: User | null;
  onClose: () => void;
  onViewModeChange: (mode: ViewMode) => void;
  onDraftChange: (updates: Partial<CollabProposal>) => void;
  onUpdate: (proposalId: string, updates: Partial<CollabProposal>, budgetIncreased?: boolean) => Promise<void>;
  onSendMessage: (proposalId: string, message: string) => Promise<void>;
}

type ViewMode = 'edit' | 'diff' | 'chat';

const CollabNegotiationModal: React.FC<CollabNegotiationModalProps> = ({
  isOpen,
  viewMode,
  proposal,
  draftProposal,
  previousProposal,
  currentUser,
  onClose,
  onViewModeChange,
  onDraftChange,
  onUpdate,
  onSendMessage,
}) => {
  const [message, setMessage] = useState('');
  const isProposer = useMemo(() => currentUser?.id === proposal?.proposerId, [currentUser, proposal]);

  const isNegotiationActive = proposal?.status === ProposalStatus.DRAFT || proposal?.status === ProposalStatus.NEGOTIATION || proposal?.status === ProposalStatus.PENDING;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    onDraftChange({ [name]: value });
  };
  
  const handleSendMessage = async () => {
    if (message.trim() && proposal) {
      await onSendMessage(proposal.id, message);
      setMessage('');
    }
  };

  const handleUpdate = () => {
    if (proposal && draftProposal) {
      const updates: Partial<CollabProposal> = {};
      const currentBudget = parseFloat(draftProposal.budget || '0');
      const initialBudgetFloat = parseFloat(proposal.budget || '0');

      if (draftProposal.budget !== proposal.budget) {
        updates.budget = draftProposal.budget;
      }
      const originalDescription = proposal.description || proposal.contractData?.content;
      if ((draftProposal.description || '') !== (originalDescription || '')) {
          updates.description = draftProposal.description;
      }
      
      if (Object.keys(updates).length > 0) {
        const budgetIncreased = isProposer && currentBudget > initialBudgetFloat;
        onUpdate(proposal.id, updates, budgetIncreased);
      } else {
        onClose();
      }
    }
  };

  const diffHtml = useMemo(() => {
    let originalContent = '';
    let editedContent = '';

    if (isProposer) {
        if (proposal && draftProposal) {
            const originalDescription = proposal.description || proposal.contractData?.content || '';
            originalContent = `Budget: ${proposal.budget}\n\n${originalDescription}`;
            const editedDescription = draftProposal.description || '';
            editedContent = `Budget: ${draftProposal.budget}\n\n${editedDescription}`;
        }
    } else {
        if (previousProposal && proposal) {
            const originalDescription = previousProposal.description || previousProposal.contractData?.content || '';
            originalContent = `Budget: ${previousProposal.budget}\n\n${originalDescription}`;
            const editedDescription = proposal.description || proposal.contractData?.content || '';
            editedContent = `Budget: ${proposal.budget}\n\n${editedDescription}`;
        }
    }
    
    return createDiffHtml(originalContent, editedContent);
  }, [viewMode, proposal, previousProposal, draftProposal, isProposer]);

  if (!isOpen || !proposal || !draftProposal) {
    return null;
  }

  const messages = proposal.chatHistory || [];
  const hasMadeChanges = draftProposal.budget !== proposal.budget || (draftProposal.description || '') !== (proposal.description || proposal.contractData?.content || '');

  return (
    <AnimatePresence>
      {isOpen && (
        <>
        <style>{`
          .diff-view del { background-color: transparent !important; color: #ff7fe2 !important; text-decoration: line-through !important; }
          .diff-view ins { background-color: transparent !important; color: #8cff8c !important; text-decoration: none !important; }
        `}</style>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.9, y: -30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 30 }}
              className="bg-kala-800 border border-kala-700 rounded-2xl shadow-2xl w-full max-w-6xl h-[95vh] flex flex-col text-white"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="flex items-center justify-between p-4 border-b border-kala-700 bg-kala-900/50 rounded-t-2xl">
                <div className="flex items-center gap-3">
                  <FileText className="text-kala-secondary"/>
                  <h2 className="text-xl font-bold text-white">{isNegotiationActive ? `Negotiate: ${proposal.eventName}` : `View: ${proposal.eventName}`}</h2>
                </div>
                <button onClick={onClose} className="p-2 rounded-full text-kala-300 hover:bg-kala-700 transition-colors"><X size={22} /></button>
              </header>

              <main className="flex-grow p-6 overflow-y-auto">
                  <div className="border-b border-kala-700 mb-4">
                      <nav className="-mb-px flex gap-4">
                          {isProposer && <button onClick={() => onViewModeChange('edit')} className={cn("py-2 px-1 border-b-2 font-semibold text-sm flex items-center", viewMode === 'edit' ? 'border-kala-secondary text-kala-secondary' : 'border-transparent text-kala-400 hover:text-white')}><Edit size={16} className="mr-2"/>Edit Proposal</button>}
                          {!isProposer && <button onClick={() => onViewModeChange('diff')} className={cn("py-2 px-1 border-b-2 font-semibold text-sm flex items-center", viewMode === 'diff' ? 'border-kala-secondary text-kala-secondary' : 'border-transparent text-kala-400 hover:text-white')}><Eye size={16} className="mr-2"/>Review Changes</button>}
                          {hasMadeChanges && <button onClick={() => onViewModeChange('diff')} className={cn("py-2 px-1 border-b-2 font-semibold text-sm flex items-center", viewMode === 'diff' ? 'border-kala-secondary text-kala-secondary' : 'border-transparent text-kala-400 hover:text-white')}><Eye size={16} className="mr-2"/>Review Changes</button>}
                          <button onClick={() => onViewModeChange('chat')} className={cn("py-2 px-1 border-b-2 font-semibold text-sm flex items-center", viewMode === 'chat' ? 'border-kala-secondary text-kala-secondary' : 'border-transparent text-kala-400 hover:text-white')}><MessageSquare size={16} className="mr-2"/>Chat</button>
                      </nav>
                  </div>

                  <AnimatePresence mode="wait">
                      <motion.div key={viewMode} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                        {viewMode === 'edit' && (
                            <div className="space-y-6">
                              <div>
                                  <label htmlFor="budget" className="text-sm font-medium text-kala-300">Budget</label>
                                  <input id="budget" name="budget" type="number" value={draftProposal?.budget || ''} onChange={handleInputChange} readOnly={!isProposer || !isNegotiationActive} className="w-full p-2 border rounded mt-1 bg-kala-900 border-kala-600 focus:outline-none read-only:opacity-75 read-only:cursor-not-allowed" />
                              </div>
                              <div>
                                  <label htmlFor="description" className="text-sm font-medium text-kala-300">Scope of Work</label>
                                  <textarea id="description" name="description" rows={15} value={draftProposal?.description || ''} onChange={handleInputChange} readOnly={!isProposer || !isNegotiationActive} className="w-full p-2 border rounded mt-1 bg-kala-900 border-kala-600 font-mono text-sm resize-y focus:outline-none read-only:opacity-75 read-only:cursor-not-allowed" />
                              </div>
                            </div>
                        )}
                        {viewMode === 'diff' && (
                            <div className="diff-view border rounded-md p-4 bg-transparent overflow-y-auto h-full prose prose-sm max-w-none prose-invert"><pre className="whitespace-pre-wrap bg-transparent p-0" dangerouslySetInnerHTML={{ __html: diffHtml }} /></div>
                        )}
                        {viewMode === 'chat' && (
                            <div className="flex flex-col h-[70vh]">
                                <div className="flex-grow bg-kala-900/50 p-3 rounded-lg overflow-y-auto border border-kala-700 mb-4">
                                    {messages.length === 0 && <div className="text-center text-kala-400">No messages yet.</div>}
                                    {messages.map(msg => (<div key={msg.id || `msg-${Math.random()}`} className={cn("p-2 my-1 rounded-lg text-sm w-fit max-w-[80%]", msg.senderId === currentUser?.id ? 'bg-blue-600/30 text-right ml-auto' : 'bg-kala-700/50 mr-auto')}><p className="text-left">{msg.text}</p><span className="text-xs text-kala-400">{new Date(msg.timestamp).toLocaleTimeString()}</span></div>))}
                                </div>
                                <div className="relative">
                                    <input type="text" value={message} onChange={(e) => setMessage(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()} placeholder={"Type a message..."} className="w-full p-3 pr-12 border-2 rounded-lg bg-kala-900 border-kala-600 focus:border-kala-secondary outline-none transition disabled:opacity-50" />
                                    <button onClick={handleSendMessage} disabled={!message.trim()} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-kala-secondary text-kala-900 hover:bg-purple-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"><Send size={18} /></button>
                                </div>
                            </div>
                        )}
                      </motion.div>
                  </AnimatePresence>
              </main>
              
              <footer className="p-4 border-t border-kala-700 bg-kala-900/50 rounded-b-2xl">
                  <div className="flex justify-between items-center">
                      <p className='text-sm text-kala-400'>{isProposer ? 'Propose changes to the budget and scope.' : 'Review changes and chat.'}</p>
                      <div className="flex items-center gap-3">
                          <button onClick={onClose} className="btn-secondary">Cancel</button>
                          {isProposer && isNegotiationActive && <button onClick={handleUpdate} disabled={!hasMadeChanges} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">Submit Updated Proposal</button>}
                      </div>
                  </div>
              </footer>

            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CollabNegotiationModal;
